import React from 'react';
import { Truck, Package, Clock, MapPin, CheckCircle, DollarSign } from 'lucide-react';

const ShippingInfoPage: React.FC = () => {
  const shippingOptions = [
    {
      name: 'Standard Delivery',
      price: '$4.99',
      time: '1-2 Business Days',
      description: 'Perfect for regular orders',
      icon: Package,
      features: ['Same-day dispatch', 'Trackable', 'Signature required'],
    },
    {
      name: 'Express Delivery',
      price: '$9.99',
      time: 'Same Day',
      description: 'For urgent deliveries',
      icon: Truck,
      features: ['Priority processing', 'Before 5 PM delivery', 'Live tracking'],
    },
    {
      name: 'Free Delivery',
      price: 'FREE',
      time: '1-2 Business Days',
      description: 'On orders over $50',
      icon: DollarSign,
      features: ['No minimum order', 'Standard delivery', 'All areas covered'],
    },
  ];

  const deliveryAreas = [
    { area: 'Harare CBD', delivery: 'Same Day' },
    { area: 'Northern Suburbs', delivery: 'Same Day' },
    { area: 'Western Suburbs', delivery: '1-2 Days' },
    { area: 'Eastern Suburbs', delivery: '1-2 Days' },
    { area: 'Greater Harare', delivery: '2-3 Days' },
  ];

  const trackingSteps = [
    {
      step: 'Order Confirmed',
      description: 'You\'ll receive an email with your order details',
      time: 'Immediately',
    },
    {
      step: 'Order Processing',
      description: 'We prepare your items for dispatch',
      time: '1-2 hours',
    },
    {
      step: 'Dispatched',
      description: 'Your order leaves our facility',
      time: 'Same day',
    },
    {
      step: 'Out for Delivery',
      description: 'Driver is en route to your address',
      time: 'As scheduled',
    },
    {
      step: 'Delivered',
      description: 'Order successfully delivered',
      time: 'End of day',
    },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Truck className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Shipping Information
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Fast, reliable delivery to your doorstep
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Shipping Options */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white dark:text-dark-dark text-center mb-8 transition-colors duration-300">
            Shipping Options
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {shippingOptions.map((option) => (
              <div
                key={option.name}
                className="p-6 rounded-lg border-2 border-dark-light dark:border-gray-200 hover:border-accent dark:hover:border-primary transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center transition-colors duration-300">
                    <option.icon className="w-6 h-6 text-accent dark:text-primary transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white dark:text-dark-dark transition-colors duration-300">
                      {option.name}
                    </h3>
                    <p className="text-accent dark:text-primary font-bold transition-colors duration-300">
                      {option.price}
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 dark:text-gray-600 mb-4 transition-colors duration-300">
                  {option.description}
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-300 dark:text-gray-600 transition-colors duration-300">
                    {option.time}
                  </span>
                </div>
                <ul className="space-y-2">
                  {option.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-accent dark:text-primary flex-shrink-0" />
                      <span className="text-sm text-gray-300 dark:text-gray-600 transition-colors duration-300">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Delivery Areas */}
          <div>
            <h2 className="text-2xl font-bold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
              Delivery Areas & Times
            </h2>
            <div className="bg-dark-light dark:bg-gray-50 rounded-lg overflow-hidden transition-colors duration-300">
              <div className="grid grid-cols-2 gap-0">
                <div className="bg-accent/10 dark:bg-primary/10 p-4 font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                  Area
                </div>
                <div className="bg-accent/10 dark:bg-primary/10 p-4 font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                  Delivery Time
                </div>
                {deliveryAreas.map((item) => (
                  <React.Fragment key={item.area}>
                    <div className="p-4 border-b border-dark dark:border-gray-200 text-white dark:text-dark-dark transition-colors duration-300">
                      {item.area}
                    </div>
                    <div className="p-4 border-b border-dark dark:border-gray-200 text-gray-300 dark:text-gray-600 transition-colors duration-300">
                      {item.delivery}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
              <MapPin className="w-4 h-4" />
              <span>Currently serving Harare metropolitan area only</span>
            </div>
          </div>

          {/* Order Tracking */}
          <div>
            <h2 className="text-2xl font-bold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
              Track Your Order
            </h2>
            <div className="space-y-4">
              {trackingSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-accent dark:bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 transition-colors duration-300">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white dark:text-dark-dark mb-1 transition-colors duration-300">
                      {step.step}
                    </h3>
                    <p className="text-sm text-gray-300 dark:text-gray-600 mb-1 transition-colors duration-300">
                      {step.description}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">
                      <Clock className="w-3 h-3" />
                      <span>{step.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'What are your delivery hours?',
                a: 'We deliver Monday to Saturday, 8 AM to 8 PM. Sunday deliveries are available for urgent orders.',
              },
              {
                q: 'Can I change my delivery address?',
                a: 'Yes, you can update your delivery address before your order is dispatched. Contact support for assistance.',
              },
              {
                q: 'What if I\'m not home for delivery?',
                a: 'Our driver will attempt delivery twice. If unsuccessful, we\'ll contact you to reschedule.',
              },
              {
                q: 'Do you deliver to PO Boxes?',
                a: 'No, we require a physical address for delivery to ensure proper handling of fresh products.',
              },
              {
                q: 'Can I schedule a specific delivery time?',
                a: 'Yes, express delivery allows you to choose a 2-hour delivery window.',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="p-4 bg-dark-light dark:bg-gray-50 rounded-lg transition-colors duration-300"
              >
                <h3 className="font-semibold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
                  {item.q}
                </h3>
                <p className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="text-center p-8 bg-accent/10 dark:bg-primary/10 rounded-lg border border-accent/30 dark:border-primary/30 transition-colors duration-300">
          <Truck className="w-12 h-12 text-accent dark:text-primary mx-auto mb-4 transition-colors duration-300" />
          <h3 className="text-xl font-semibold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
            Need Shipping Help?
          </h3>
          <p className="text-gray-300 dark:text-gray-600 mb-6 max-w-2xl mx-auto transition-colors duration-300">
            Our delivery team is ready to answer your questions and ensure your order arrives fresh and on time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+263771234567"
              className="inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 bg-accent dark:bg-primary text-white hover:bg-accent-dark dark:hover:bg-primary-dark transition-colors duration-300"
            >
              Call Delivery Team
            </a>
            <a
              href="mailto:delivery@angelcite.com"
              className="inline-flex items-center justify-center font-medium rounded-lg px-6 py-3 border-2 border-accent dark:border-primary text-accent dark:text-primary hover:bg-accent dark:hover:bg-primary hover:text-white transition-colors duration-300"
            >
              Email Delivery Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingInfoPage;